/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { FilterMenuTranslationsQuery } from '../../../../../graphql-types';
import { usePageContext } from '../../../../hooks/contexts/usePageContext';
import { UnderlineButton } from '../../underlineButton/UnderlineButton';
import { FilledButton } from '../../filledButton/FilledButton';
import { CursorSlider } from '../../cursorSlider/CursorSlider';
import { MinMaxCriterias, Filters, SanityFilters, FilterDate } from '../../../../utils/types';
import * as styles from './filterMenuDesktop.module.scss';
import moment from 'moment';

interface IFilterMenuDesktopProps {
  sanityFilters: SanityFilters;
  minMaxCriterias: MinMaxCriterias;
  onValidateFilters?: (filters: Filters) => void;
  onChangeFilters?: (filters: Filters, filterDate?: FilterDate) => void;
  filterDate?: boolean;
  initialDatevalue?: FilterDate;
}

const reformatNewLine = (str: string | undefined) => str?.replace('\n', '<br />');

export const initializeFilters = (
  sanityFilters: SanityFilters,
  lang: string,
  minMaxCriterias: MinMaxCriterias
) =>
  sanityFilters?.filters?.reduce((acc, current) => {
    if (current && current) {
      acc?.push({
        label: current?._rawLabel[lang],
        criteria: current?.criteria,
        type: current?.type ?? '',
        active: current?.type === 'cursor' || current.active ? true : false,
        filterValue:
          current?.type === 'cursor'
            ? {
                min: minMaxCriterias?.[current?.criteria]?.min,
                max: minMaxCriterias?.[current?.criteria]?.max,
              }
            : false,
      });
    }
    return acc;
  }, [] as Filters);

export const FilterMenuDesktop = ({
  sanityFilters,
  minMaxCriterias,
  onValidateFilters,
  onChangeFilters,
  filterDate,
  initialDatevalue,
}: IFilterMenuDesktopProps) => {
  const { lang } = usePageContext();
  const { sanityTranslations } = useStaticQuery<FilterMenuTranslationsQuery>(QUERY);
  const [filters, setFilters] = useState<Filters>(
    initializeFilters(sanityFilters, lang, minMaxCriterias)
  );

  const today = initialDatevalue ? initialDatevalue.dateMin : moment().format(`yyyy-MM-DD`);
  const todayPlus2Week = initialDatevalue
    ? initialDatevalue.dateMax
    : moment().add(2, 'week').format(`yyyy-MM-DD`);

  const [minDate, setMinDate] = useState<string>(today);
  const [maxDate, setMaxDate] = useState<string>(todayPlus2Week);

  const setActive = (filter, active) => {
    if (filters) setFilters(filters.map((f) => (f === filter ? { ...f, active } : { ...f })));
  };

  const unselectAll = (selectedFilters: Filters) => {
    setFilters(selectedFilters?.map((filter) => ({ ...filter, active: false })));
  };

  const handleCursorChange = (min: number, max: number, value: string | undefined) => {
    setFilters(
      filters?.map((filter) =>
        filter.criteria === value ? { ...filter, filterValue: { min, max } } : { ...filter }
      )
    );
  };

  const handleFilterDate = (e: React.ChangeEvent<HTMLInputElement>, type: 'min' | 'max') => {
    if (onChangeFilters) {
      const activeFilters = filters?.filter((x) => x.active);
      if (type === 'min') {
        setMinDate(e?.target?.value);
        onChangeFilters(activeFilters, { dateMin: e?.target?.value, dateMax: maxDate });
      } else if (type === 'max') {
        setMaxDate(e?.target?.value);
        onChangeFilters(activeFilters, { dateMin: minDate, dateMax: e?.target?.value });
      }
    }
  };
  // (type: 'min' | 'max') =>
  // (e: React.ChangeEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>) => {
  // };

  useEffect(() => {
    const activeFilters = filters?.filter((x) => x.active);
    if (onChangeFilters) onChangeFilters(activeFilters, { dateMin: minDate, dateMax: maxDate });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // debugger;
  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.titleTabMob}>
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: reformatNewLine(sanityTranslations?._rawModalTitleMobile?.[lang]) ?? '',
            }}
          />
        </div>
      </div>
      <div className={styles.contentWrapper}>
        <h3 className={styles.title}>{sanityTranslations?._rawModalTitleDesktop[lang]}</h3>
        <ul className={styles.list}>
          {filterDate && (
            <li key="li-date-filter-group-key" className={styles.dateFilterGroup}>
              <h5>{sanityTranslations?._rawTitleFilterDate?.[lang]}</h5>
              <div className={styles.dateFilter}>
                <input
                  type="date"
                  max={maxDate}
                  value={minDate}
                  min={today}
                  onChange={(e) => handleFilterDate(e, 'min')}
                  onKeyDown={(e) =>
                    handleFilterDate(e as unknown as React.ChangeEvent<HTMLInputElement>, 'min')
                  }
                />
                <span>-</span>
                <input
                  type="date"
                  min={today}
                  value={maxDate}
                  onChange={(e) => handleFilterDate(e, 'max')}
                  onKeyDown={(e) =>
                    handleFilterDate(e as unknown as React.ChangeEvent<HTMLInputElement>, 'max')
                  }
                />
              </div>
            </li>
          )}
          {filters && filterDate && <h5>{sanityTranslations?._rawTitleFilterTheme?.[lang]}</h5>}
          {filters?.map((x) => {
            switch (x.type) {
              case 'cursor':
                return (
                  <li key={x.criteria}>
                    <div className={styles.cursor}>{x.label}</div>
                    <CursorSlider
                      min={x.criteria ? minMaxCriterias?.[x.criteria]?.min : 0}
                      max={x.criteria ? minMaxCriterias?.[x.criteria]?.max : 0}
                      onChange={({ min, max }) => handleCursorChange(min, max, x.criteria)}
                    />
                  </li>
                );
              case 'checkbox':
                return (
                  <div
                    key={x.criteria}
                    className={styles.item}
                    onClick={() => setActive(x, !x.active)}
                    onKeyDown={() => setActive(x, !x.active)}
                    role="presentation"
                  >
                    <div className={styles.check}>
                      {x.active ? <div className={styles.redbox} /> : null}
                      <input type="checkbox" checked={x.active} readOnly />
                    </div>
                    <label>{x.label}</label>
                  </div>
                );
              case 'title':
                return <div className={styles.cursor}>{x.label}</div>;
              default:
                return null;
            }
          })}
          {/* {filters?.map((x) => 
            x.type === 'cursor' ? (
              <li key={x.criteria}>
                <div className={styles.cursor}>{x.label}</div>
                <CursorSlider
                  min={x.criteria ? minMaxCriterias?.[x.criteria]?.min : 0}
                  max={x.criteria ? minMaxCriterias?.[x.criteria]?.max : 0}
                  onChange={({ min, max }) => handleCursorChange(min, max, x.criteria)}
                />
              </li>
            ) : (
              <div
                key={x.criteria}
                className={styles.item}
                onClick={() => setActive(x, !x.active)}
                onKeyDown={() => setActive(x, !x.active)}
                role="presentation"
              >
                <div className={styles.check}>
                  {x.active ? <div className={styles.redbox} /> : null}
                  <input type="checkbox" checked={x.active} readOnly />
                </div>
                <label>{x.label}</label>
              </div>
            )
          )} */}
        </ul>
        <div className={styles.actions}>
          <UnderlineButton
            onClick={() => {
              unselectAll(filters);
            }}
          >
            {sanityTranslations?._rawUnselect?.[lang]}
          </UnderlineButton>
          <FilledButton onClick={() => onValidateFilters && onValidateFilters(filters)}>
            {sanityTranslations?._rawValidate?.[lang]}
          </FilledButton>
        </div>
      </div>
    </div>
  );
};

const QUERY = graphql`
  query FilterMenuTranslations {
    sanityTranslations {
      _rawModalTitleDesktop
      _rawModalTitleMobile
      _rawUnselect
      _rawValidate
      _rawModalTitleDesktop
      _rawTitleFilterTheme
      _rawTitleFilterDate
    }
  }
`;
